import { useState, useEffect, useRef, useCallback } from 'react';
import Slide from './Slide';
import Icon from './Icon';
import { SLIDES, SLIDE_INTERVAL } from '../constants';
import styles from './HeroSlider.module.css';

export default function HeroSlider({ onAccentChange }) {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const sliderRef = useRef(null);

  const goTo = useCallback(
    (idx) => {
      const next = ((idx % SLIDES.length) + SLIDES.length) % SLIDES.length;
      setCurrent(next);
      onAccentChange(SLIDES[next].accent);
    },
    [onAccentChange]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (!paused) {
      intervalRef.current = setInterval(next, SLIDE_INTERVAL);
    }
    return () => clearInterval(intervalRef.current);
  }, [next, paused]);

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      sliderRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Swipe en móvil
  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const accent = SLIDES[current].accent;

  return (
    <div
      ref={sliderRef}
      className={`${styles.slider} ${isFullscreen ? styles.fullscreen : ''}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {SLIDES.map((slide, i) => (
        <Slide key={slide.id} slide={slide} active={i === current} />
      ))}

      {/* Flechas */}
      <button
        className={styles.arrow}
        style={{ left: 16 }}
        onClick={prev}
        aria-label="Diapositiva anterior"
      >
        <Icon name="chevronLeft" size={20} color="#fff" />
      </button>
      <button
        className={styles.arrow}
        style={{ right: 16 }}
        onClick={next}
        aria-label="Diapositiva siguiente"
      >
        <Icon name="chevronRight" size={20} color="#fff" />
      </button>

      {/* Dots */}
      <div className={styles.dots}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={styles.dot}
            aria-label={`Ir a diapositiva ${i + 1}`}
            onClick={() => goTo(i)}
            style={{
              width: i === current ? 28 : 8,
              background: i === current ? accent : 'rgba(255,255,255,0.28)',
            }}
          />
        ))}
      </div>

      {/* Contador */}
      <span className={styles.counter}>
        {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
      </span>

      {/* Fullscreen */}
      <button
        className={styles.fsBtn}
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
      >
        <Icon name={isFullscreen ? 'minimize' : 'fullscreen'} size={16} color="#fff" />
      </button>

      {/* Barra de progreso */}
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          key={current}
          style={{ background: accent, animationDuration: `${SLIDE_INTERVAL}ms` }}
        />
      </div>
    </div>
  );
}
